/**
 * Publications List Component
 * Displays a list of publication cards with search functionality
 * Uses GraphQL Codegen types for type safety
 */

'use client';

import { usePublicationSearch } from '../../hooks';
import type { PublicationTeaserType } from '../../types/publication';
import { PublicationCard } from '../publication-card';
import { SearchInput } from '../search-input';

interface PublicationsListProps {
  publications: PublicationTeaserType[];
  /** Show search input (default: true) */
  showSearch?: boolean;
}

export function PublicationsList({ publications, showSearch = true }: PublicationsListProps) {
  const {
    query,
    setQuery,
    filteredPublications,
    totalResults,
    hasResults,
    hasActiveSearch,
  } = usePublicationSearch({ publications });

  if (!publications || publications.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
        No publications available at the moment.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Input */}
      {showSearch && (
        <div className="flex items-center justify-between gap-4">
          <SearchInput
            query={query}
            onQueryChange={setQuery}
            placeholder="Search by title, description, reference..."
            className="w-full"
          />
          {hasActiveSearch && (
            <p className="text-sm text-muted-foreground">
              {totalResults} {totalResults === 1 ? 'result' : 'results'} found
            </p>
          )}
        </div>
      )}

      {/* Screen reader announcement for search results */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {hasResults
          ? `${totalResults} ${totalResults === 1 ? 'publication' : 'publications'} found`
          : hasActiveSearch
            ? 'No publications match your search'
            : 'No publications available'}
      </div>

      {/* Publications Grid */}
      {hasResults ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {filteredPublications.map(publication => (
            <PublicationCard 
              key={publication.id} 
              publication={publication}
              searchTerm={query}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/40 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {hasActiveSearch
              ? 'No publications match your search.'
              : 'No publications available at the moment.'}
          </p>
          {hasActiveSearch && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="mt-4 text-sm font-medium text-primary hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      )}
    </div>
  );
}

